import { Signal } from "@char/aftercare";
import { quoteUri } from "../lib/model.ts";
import { threadTree } from "../lib/tree.ts";
import { postUri } from "../lib/uri.ts";
import { Thread } from "./load.ts";
import { postCard } from "./post.tsx";
import { followPost } from "./scroll.ts";

const app = document.getElementById("app")!;
let cancelLoad = () => {};
let selectInThread = (_hash: string) => false;

function renderRoute() {
  if (selectInThread(location.hash)) return;
  cancelLoad();
  cancelLoad = () => {};
  selectInThread = () => false;
  document.querySelector<HTMLDialogElement>("dialog.image-viewer[open]")
    ?.close();
  let routeHash = location.hash;
  let input = "";
  let error = "";
  try {
    if (routeHash && routeHash !== "#/") {
      if (!routeHash.startsWith("#/")) {
        throw new Error("Expected #/at://did/collection/rkey.");
      }
      input = routeHash.slice(2);
    } else if (!routeHash) {
      input = new URL(location.href).searchParams.get("post") ?? "";
    }
    if (input) {
      input = postUri(input).toString();
      routeHash = `#/${input}`;
      history.replaceState(null, "", location.pathname + routeHash);
    }
  } catch (cause) {
    error = cause instanceof Error ? cause.message : "Invalid post link.";
  }
  const field = (
    <input
      id="post"
      name="post"
      type="text"
      required
      value={input}
      placeholder="bsky.app post link or at:// URI"
      spellcheck={false}
      autocomplete="off"
      _oninput={(event) =>
        (event.currentTarget as HTMLInputElement).setCustomValidity("")}
    />
  ) as HTMLInputElement;
  app.replaceChildren(
    <form
      class="entry"
      _onsubmit={(event) => {
        event.preventDefault();
        try {
          const hash = `#/${postUri(field.value).toString()}`;
          if (location.hash === hash) renderRoute();
          else location.hash = hash;
        } catch (cause) {
          field.setCustomValidity(
            cause instanceof Error ? cause.message : "Invalid post link.",
          );
          field.reportValidity();
        }
      }}
    >
      <label htmlFor="post">Post URL</label>
      <div class="input-row">
        {field}
        <button type="submit">Load</button>
      </div>
    </form>,
  );

  if (error) {
    app.append(<p class="route-error" role="alert">{error}</p>);
    return;
  }
  if (!input) field.focus();
  else {
    const thread = new Thread(input);
    let selected = "";
    const status = new Signal("Resolving post…");
    const busy = new Signal(false);
    let controller: AbortController;
    const count = new Signal("");
    const list = (
      <ol class="thread" ariaLabel="Thread posts, oldest replies first" />
    );
    const rendered = new Map<
      string,
      { signature: string; element: HTMLLIElement }
    >();
    const continueButton = (
      <button
        type="button"
        hidden={busy}
        _onclick={() => {
          void run();
        }}
      >
        Continue
      </button>
    ) as HTMLButtonElement;
    const stopButton = (
      <button
        type="button"
        hidden={busy.derive((value) => !value)}
        _onclick={() => controller.abort()}
      >
        Stop
      </button>
    );
    app.append(
      <section class="thread-tools">
        <div class="toolbar">
          <span>{count}</span>
          {stopButton}
          {continueButton}
          <button type="button" _onclick={() => location.reload()}>
            Reload
          </button>
        </div>
        <div role="status" ariaLive="polite">{status}</div>
      </section>,
      list,
    );
    let stopFollowing = followPost(app, () => rendered.get(selected)?.element);
    selectInThread = (hash) => {
      if (!hash.startsWith("#/")) return false;
      const uri = hash.slice(2);
      const target = rendered.get(uri)?.element;
      if (!target) return false;
      document.querySelector<HTMLDialogElement>("dialog.image-viewer[open]")
        ?.close();
      rendered.get(selected)?.element.classList.remove("selected");
      selected = uri;
      routeHash = hash;
      field.value = uri;
      field.setCustomValidity("");
      target.classList.add("selected");
      stopFollowing();
      stopFollowing = followPost(app, () => rendered.get(selected)?.element);
      return true;
    };
    cancelLoad = () => {
      stopFollowing();
      controller?.abort();
    };

    const run = async () => {
      if (busy.get()) return;
      controller = new AbortController();
      busy.set(true);
      status.set(thread.root ? "Loading…" : "Resolving post…");
      try {
        await thread.load(controller.signal, (progress) => {
          if (location.hash !== routeHash && !selectInThread(location.hash)) {
            controller.abort();
            return;
          }
          selected ||= thread.selected;
          const readable = [...thread.posts.values()].filter((post) =>
            post.record
          ).length;
          count.set(`${readable} ${readable === 1 ? "post" : "posts"}`);
          status.set(progress);
          const rows = threadTree(thread.root, thread.posts);
          let position = list.firstChild;
          for (const [index, row] of rows.entries()) {
            const did = postUri(row.post.uri, true).host;
            const quote = row.post.record && quoteUri(row.post.record);
            const quoted = quote && thread.quotes.get(quote);
            const quotedAuthor = quoted &&
              thread.authors.get(postUri(quoted.uri, true).host);
            const parentAuthor = row.parent &&
              thread.authors.get(postUri(row.parent, true).host);
            const parentName = parentAuthor && (parentAuthor.handle
              ? `@${parentAuthor.handle}`
              : parentAuthor.displayName);
            const signature = JSON.stringify([
              row.post,
              row.parent,
              row.issue,
              row.connectedToParent,
              parentName,
              thread.authors.get(did),
              quoted,
              quotedAuthor,
            ]);
            let saved = rendered.get(row.post.uri);
            if (!saved || saved.signature !== signature) {
              const element = (
                <li id={row.post.uri}>
                  {row.parent && !row.connectedToParent
                    ? (
                      <div class="post-context">
                        <a href={`#/${row.parent}`}>
                          Reply to {parentName || "parent"}
                        </a>
                      </div>
                    )
                    : undefined}
                  {row.detached && row.depth === 0
                    ? (
                      <p class="branch-note">
                        {row.issue ?? "Parent path unavailable."}
                      </p>
                    )
                    : undefined}
                  {postCard(row.post, thread.authors, thread.quotes)}
                </li>
              ) as HTMLLIElement;
              if (saved) {
                if (position === saved.element) position = element;
                saved.element.replaceWith(element);
              }
              saved = { signature, element };
              rendered.set(row.post.uri, saved);
            }
            const connectedBefore = row.connectedToParent;
            const connectedAfter = rows[index + 1]?.connectedToParent &&
              rows[index + 1].parent === row.post.uri;
            saved.element.className = `branch${
              row.post.uri === selected ? " selected" : ""
            }${connectedBefore ? " connected-before" : ""}${
              connectedAfter ? " connected-after" : ""
            }`;
            saved.element.style.setProperty(
              "--avatar-y",
              row.parent && !row.connectedToParent ? "52px" : "32px",
            );
            if (position !== saved.element) {
              list.insertBefore(saved.element, position);
            }
            position = saved.element.nextSibling;
          }
          const visible = new Set(rows.map((row) => row.post.uri));
          for (const [uri, saved] of rendered) {
            if (!visible.has(uri)) {
              saved.element.remove();
              rendered.delete(uri);
            }
          }
          if (selected && location.hash !== `#/${selected}`) {
            routeHash = `#/${selected}`;
            history.replaceState(null, "", location.pathname + routeHash);
            field.value = selected;
          }
        });
        const missing = [...thread.posts.values()].filter((post) =>
          !post.record
        ).length;
        status.set(
          `Loaded all indexed replies.${
            missing ? ` ${missing} unavailable.` : ""
          }`,
        );
      } catch (error) {
        status.set(
          controller.signal.aborted
            ? "Paused."
            : `${
              error instanceof Error ? error.message : "Loading failed."
            } Received posts are kept.`,
        );
      } finally {
        busy.set(false);
        continueButton.hidden = thread.phase === "done";
      }
    };
    void run();
  }
}

addEventListener("hashchange", renderRoute);
addEventListener("pagehide", () => cancelLoad());
renderRoute();
